import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    ComposedChart, Line, Area, Legend
} from 'recharts';
import {
    TrendingUp, MousePointerClick, Eye, QrCode,
    ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight, Zap, Calendar, Activity
} from 'lucide-react';

interface KPI {
    total_views: number;
    qr_scans: number;
    total_clicks: number;
    ctr: number;
}

interface ChartDataPoint {
    date: string;
    views: number;
    clicks: number;
}

interface TopLink {
    link_type: string;
    link_id: string;
    clicks: number;
}

interface TopLinksByDate {
    date: string;
    interactions: TopLink[];
}

interface AnalyticsData {
    kpis: KPI;
    chart_data: ChartDataPoint[];
    top_links_by_date: TopLinksByDate[];
}

export const Analytics: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<AnalyticsData | null>(null);
    const [expandedDates, setExpandedDates] = useState<Record<string, boolean>>({});
    const [dateRange, setDateRange] = useState('30d');

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const response = await api.get('/analytics/overview');
            if (response.data.success && response.data.data) {
                setData(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load analytics', error);
            toast.error('Failed to load analytics data.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
        return new Date(dateStr).toLocaleDateString(undefined, options);
    };

    // Derived Insights without changing API
    const insights = useMemo(() => {
        if (!data) return [];
        const topLink = data.top_links_by_date.flatMap(d => d.interactions)
            .reduce((max, current) => (max.clicks > current.clicks) ? max : current, { link_type: '', link_id: '', clicks: -1 });
        
        const sortedData = [...data.chart_data].sort((a,b) => b.views - a.views);
        const bestDay = sortedData.length > 0 ? formatDate(sortedData[0].date) : 'N/A';

        return [
            { title: "Top Interaction", value: topLink.clicks > 0 ? topLink.link_type : 'None', detail: `${topLink.clicks > 0 ? topLink.clicks : 0} clicks` },
            { title: "Best Performing Day", value: bestDay, detail: 'Most profile views' },
            { title: "Current CTR", value: `${data.kpis.ctr}%`, detail: 'Overall conversion' }
        ];
    }, [data]);


    if (loading) {
        return (
            <div className="flex justify-center items-center h-full min-h-[60vh]">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="bg-[#FAFAFA] min-h-[calc(100vh-4rem)] p-4 sm:p-6 lg:p-8 flex items-center justify-center">
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm max-w-md w-full">
                    <div className="w-20 h-20 bg-blue-50/50 rounded-full flex items-center justify-center mx-auto mb-5">
                        <Activity className="h-8 w-8 text-blue-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Analytics Data Yet</h3>
                    <p className="text-gray-500 text-sm">Once visitors start viewing and interacting with your profile, your metrics will appear here.</p>
                </div>
            </div>
        );
    }

    const { kpis, chart_data, top_links_by_date } = data;

    // Custom Tooltip for Chart
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-gray-100 min-w-[150px]">
                    <p className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-100">{label}</p>
                    {payload.map((entry: any, index: number) => (
                        <div key={index} className="flex items-center justify-between gap-4 mb-2 last:mb-0">
                            <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                                {entry.name}
                            </span>
                            <span className="text-sm font-bold text-gray-900">{entry.value}</span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-[#FAFAFA] min-h-[calc(100vh-4rem)] p-4 sm:p-6 lg:p-8 font-inter">
            <div className="max-w-[1600px] mx-auto space-y-6">
                
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Analytics</h1>
                        <p className="text-sm text-gray-500 mt-1 sm:mt-2">Monitor your performance, traffic, and audience engagement.</p>
                    </div>
                    
                    {/* Time Range Dropdown (UI Only logic as requested) */}
                    <div className="relative inline-block w-full sm:w-auto">
                        <select 
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                            className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 cursor-pointer outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all hover:bg-gray-50/50 shadow-sm"
                        >
                            <option value="7d">Last 7 Days</option>
                            <option value="30d">Last 30 Days</option>
                            <option value="90d">Last 90 Days</option>
                        </select>
                        <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {/* Metrics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {[
                        { title: 'Total Views', value: kpis.total_views, icon: Eye, color: 'blue', trend: '+12%', isPositive: true },
                        { title: 'QR Scans', value: kpis.qr_scans, icon: QrCode, color: 'purple', trend: '+5%', isPositive: true },
                        { title: 'Total Clicks', value: kpis.total_clicks, icon: MousePointerClick, color: 'green', trend: '+18%', isPositive: true },
                        { title: 'Click-Through Rate', value: `${kpis.ctr}%`, icon: TrendingUp, color: 'orange', trend: '-2%', isPositive: false },
                    ].map((metric, idx) => (
                        <div key={idx} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                            {/* Subtle background glow effect on hover */}
                            <div className={`absolute -right-10 -top-10 w-32 h-32 bg-${metric.color}-50 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                            
                            <div className="flex justify-between items-start mb-4 relative z-10">
                                <h3 className="text-sm font-semibold text-gray-500">{metric.title}</h3>
                                <div className={`p-2 bg-${metric.color}-50 rounded-xl`}>
                                    <metric.icon className={`w-5 h-5 text-${metric.color}-500`} />
                                </div>
                            </div>
                            <div className="flex items-end justify-between relative z-10">
                                <p className="text-3xl font-bold text-gray-900 tracking-tight">{metric.value}</p>
                                <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${metric.isPositive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                    {metric.isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                                    {metric.trend}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Main Content Split */}
                <div className="flex flex-col xl:flex-row gap-6">
                    
                    {/* Left Chart Area */}
                    <div className="w-full xl:w-2/3 flex flex-col gap-6">
                        
                        {/* Traffic Chart */}
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 w-full relative">
                            <div className="flex items-center justify-between mb-8">
                                <h3 className="text-lg font-bold text-gray-900">Traffic & Engagement</h3>
                            </div>
                            
                            <div className="h-[300px] w-full">
                                {chart_data.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={chart_data} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.15} />
                                                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#f3f4f6" />
                                            <XAxis 
                                                dataKey="date" 
                                                tickFormatter={formatDate} 
                                                axisLine={false} 
                                                tickLine={false}
                                                tick={{ fill: '#9ca3af', fontSize: 12, fontWeight: 500 }}
                                                dy={10}
                                                minTickGap={20}
                                            />
                                            <YAxis 
                                                axisLine={false} 
                                                tickLine={false}
                                                tick={{ fill: '#9ca3af', fontSize: 12, fontWeight: 500 }}
                                                dx={-10}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend 
                                                iconType="circle" 
                                                iconSize={8}
                                                wrapperStyle={{ paddingTop: '20px', fontSize: '13px', fontWeight: 500, color: '#4b5563' }}
                                            />
                                            <Area 
                                                type="monotone" 
                                                name="Profile Views" 
                                                dataKey="views" 
                                                stroke="#3B82F6" 
                                                strokeWidth={3}
                                                fillOpacity={1} 
                                                fill="url(#colorViews)" 
                                                activeDot={{ r: 6, strokeWidth: 2, fill: '#fff', stroke: '#3B82F6' }}
                                            />
                                            <Line 
                                                type="monotone" 
                                                name="Link Clicks" 
                                                dataKey="clicks" 
                                                stroke="#10B981" 
                                                strokeWidth={3}
                                                dot={false}
                                                activeDot={{ r: 6, strokeWidth: 2, fill: '#fff', stroke: '#10B981' }}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="h-full flex items-center justify-center text-gray-400 text-sm font-medium">
                                        Not enough traffic data to display chart.
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Insights Panel */}
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
                            <div className="col-span-1 sm:col-span-3 pb-2 border-b border-gray-50 flex items-center gap-2">
                                <Zap className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                                <h3 className="text-base font-bold text-gray-900">Key Insights</h3>
                            </div>
                            {insights.map((insight, idx) => (
                                <div key={idx} className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{insight.title}</span>
                                    <span className="text-lg font-bold text-gray-900 capitalize">{insight.value}</span>
                                    <span className="text-sm font-medium text-gray-500 mt-0.5">{insight.detail}</span>
                                </div>
                            ))}
                        </div>

                    </div>

                    {/* Right Data Table Area */}
                    <div className="w-full xl:w-1/3 flex flex-col">
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden h-full flex flex-col">
                            <div className="p-5 sm:p-6 border-b border-gray-50 bg-white sticky top-0 z-10">
                                <h3 className="text-lg font-bold text-gray-900">Top Interactions</h3>
                                <p className="text-xs text-gray-500 mt-1">Best performing links by date.</p>
                            </div>

                            <div className="flex-1 overflow-y-auto max-h-[600px] xl:max-h-none p-2 custom-scrollbar">
                                {top_links_by_date && top_links_by_date.length > 0 ? (
                                    <div className="space-y-6 p-3 sm:p-4">
                                        {top_links_by_date.map((dateGroup, dateIdx) => {
                                            const isExpanded = expandedDates[dateGroup.date] || false;
                                            const displayLinks = isExpanded ? dateGroup.interactions : dateGroup.interactions.slice(0, 4);
                                            const hasMore = dateGroup.interactions.length > 4;

                                            return (
                                                <div key={dateIdx} className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                                                        {formatDate(dateGroup.date)}
                                                    </h4>
                                                    <div className="space-y-2">
                                                        {displayLinks.map((link, idx) => {
                                                            const isSocial = link.link_type === 'social';
                                                            const isContact = link.link_type === 'contact';
                                                            const badgeColor = isSocial ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                                                isContact ? 'bg-green-100 text-green-700 border-green-200' :
                                                                link.link_type === 'upi' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                                                                'bg-gray-100 text-gray-700 border-gray-200';

                                                            return (
                                                                <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100 hover:shadow-sm hover:border-gray-200 transition-all">
                                                                    <div className="flex items-center gap-3 overflow-hidden">
                                                                        <span className={`flex-shrink-0 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${badgeColor}`}>
                                                                            {link.link_type}
                                                                        </span>
                                                                        <span className="text-sm font-semibold text-gray-900 truncate capitalize">
                                                                            {link.link_id}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex-shrink-0 ml-4 font-bold text-gray-900 bg-gray-50 px-2.5 py-1 rounded-lg text-sm">
                                                                        {link.clicks}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    
                                                    {hasMore && (
                                                        <button
                                                            onClick={() => setExpandedDates(prev => ({ ...prev, [dateGroup.date]: !isExpanded }))}
                                                            className="mt-3 w-full flex items-center justify-center gap-1 py-2 text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors rounded-lg hover:bg-white border border-transparent hover:border-gray-200"
                                                        >
                                                            {isExpanded ? (
                                                                <>View Less <ChevronUp className="w-3.5 h-3.5" /></>
                                                            ) : (
                                                                <>View {dateGroup.interactions.length - 4} More <ChevronDown className="w-3.5 h-3.5" /></>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="p-8 text-center flex flex-col items-center justify-center h-full">
                                        <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                                            <MousePointerClick className="h-5 w-5 text-gray-300" />
                                        </div>
                                        <h4 className="text-sm font-semibold text-gray-900">No Interactions</h4>
                                        <p className="text-xs text-gray-500 mt-1 max-w-[200px]">Link clicks and interactions will be listed here.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};
